import React, { useEffect, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import { Divider, Popover, Box, Typography, Avatar, Stack, ButtonBase } from '@mui/material';

import {
  InputUI,
  FormErrorUI,
  FormLabelUI,
  CheckboxUI,
  ChromeColorPickerUI,
  SelectUI,
  FileUploadDialogUI,
  ButtonUI,
} from '../Interface';
import { TagTargetList, TagType, TagTypeList } from '../../config/tag';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import { MAX_FILE_SIZE } from '../../config/fileUpload';

const TagFields = ({ viewField }) => {
  const { values, errors, setFieldValue, validateField } = useFormikContext();
  const [anchorEl, setAnchorEl] = useState(null);
  const [picturePreview, setPicturePreview] = useState(null);

  useEffect(() => {
    if (!values.picture) {
      setPicturePreview(null);
      return;
    }

    if (values.picture instanceof File) {
      const url = URL.createObjectURL(values.picture);
      setPicturePreview(url);

      return () => URL.revokeObjectURL(url);
    }

    setPicturePreview(`${process.env.REACT_APP_API_URL}/${values.picture}`);
  }, [values.picture]);

  const handlePopoverOpen = (event) => {
    if (!viewField) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'color-popover' : undefined;

  return (
    <>
      <div className="grid grid-cols-4 gap-x-4 mx-4 mb-5">
        <div className="col-span-4 md:col-span-2">
          <Field
            type="text"
            name="name"
            as={InputUI}
            fullWidth
            label={<FormLabelUI text="Megnevezés" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.name} />}
            disabled={viewField}
            InputLabelProps={{ shrink: values.name && values.name !== '' }}
          />
        </div>
        <div className="col-span-4 md:col-span-1">
          <Field
            name="tagType"
            component={SelectUI}
            fullWidth
            option={TagTypeList}
            value={values.tagType ?? ''}
            variant="standard"
            onChange={(e) => {
              const value = e.target.value;
              setFieldValue('tagType', value ?? null).then(() => {
                validateField('tagType');
              });

              setFieldValue('picture', null);
            }}
            label={<FormLabelUI text="Típus" />}
            helperText={<FormErrorUI message={errors.tagType} />}
            disabled={viewField}
          />
        </div>
        <div className="col-span-4 md:col-span-1">
          <Field
            name="tagTarget"
            component={SelectUI}
            fullWidth
            option={TagTargetList}
            value={values.tagTarget ?? ''}
            variant="standard"
            onChange={(e) => {
              const value = e.target.value;
              setFieldValue('tagTarget', value ?? null).then(() => {
                validateField('tagTarget');
              });
            }}
            label={<FormLabelUI text="Cél" />}
            helperText={<FormErrorUI message={errors.tagTarget} />}
            disabled={viewField}
          />
        </div>
        <div className="col-span-4 md:col-span-1">
          <Field
            type="text"
            name="externalCode"
            as={InputUI}
            fullWidth
            label={<FormLabelUI text="Külső azonosító" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.externalCode} />}
            disabled={viewField}
            InputLabelProps={{ shrink: values.externalCode && values.externalCode !== '' }}
          />
        </div>
        <div className="md:col-span-1 col-span-4 pt-2">
          <Field
            name="active"
            type="checkbox"
            as={CheckboxUI}
            fullWidth
            label={<FormLabelUI text="Aktív" />}
            disabled={viewField}
          />
        </div>
        <div className="md:col-span-1 col-span-4 pt-4">
          <div
            className={`${!viewField ? 'cursor-pointer' : 'cursor-not-allowed'} rounded-full shadow-[rgba(0,_0,_0,_0.54)_0px_1px_8px] w-[20px] h-[20px] mt-1`}
            style={{ background: values.color }}
            onClick={handlePopoverOpen}
          />
          <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
            anchorOrigin={{
              vertical: 'center',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'center',
              horizontal: 'left',
            }}
          >
            <ChromeColorPickerUI
              color={values.color}
              onChange={(color) => {
                setFieldValue('color', color.hex);
              }}
            />
          </Popover>
        </div>
      </div>
      {values.tagType === TagType.STICKER && (
        <Box mx={2}>
          <Divider />
          <Stack direction="row" justifyContent="space-between" my={1}>
            <Typography fontWeight="bold">Matricakép</Typography>
            <ButtonUI
              text="Kép törlése"
              size="sm"
              className="bg-danger"
              disabled={viewField || !values.picture}
              onClick={() => setFieldValue('picture', null)}
            />
          </Stack>
          <ButtonBase
            component="label"
            disabled={viewField}
            sx={{
              borderRadius: '50%',
              cursor: viewField ? 'default' : 'pointer',
            }}
          >
            <Avatar alt={values.name} src={picturePreview} sx={{ width: 150, height: 150 }}>
              {values.name?.charAt(0).toUpperCase()}
            </Avatar>

            <input
              hidden
              type="file"
              accept="image/*"
              disabled={viewField}
              onChange={(e) => {
                const file = e.target.files?.[0];

                if (!file) return;

                if (file.size > MAX_FILE_SIZE) {
                  addNotification({
                    content: 'A kiválasztott fájl mérete meghaladja az 50 MB-ot.',
                    type: NotificationType.WARNING,
                  });

                  e.target.value = '';
                  return;
                }

                setFieldValue('picture', file);

                e.target.value = '';
              }}
            />
          </ButtonBase>
        </Box>
      )}
    </>
  );
};

export default TagFields;
